<template>
  <div class="heatmap-container">
    <div class="header">
      <h1 class="title">比赛热力图</h1>
      <p class="subtitle">点击球场生成热力图</p>
      <div class="match-id-input" v-if="!hasRouteMatchId">
        <label class="match-id-label">比赛ID：</label>
        <input 
          v-model="matchId" 
          type="text" 
          class="match-id-field" 
          placeholder="请输入比赛ID"
          @input="saveMatchId"
        />
      </div>
    </div>

    <div class="content">
      <!-- 左右并排的热力图区域 -->
      <div class="fields-container">
        <!-- 主队热力图（左侧） -->
        <div class="field-wrapper">
          <div class="field-label">主队</div>
          <div class="field-container-small">
            <!-- 主客标识（在热图外面） -->
            <div class="team-badge team-badge-left">{{ (selectedPeriod === 2) !== isSwapped ? '客队' : '主队' }}</div>
            <div class="team-badge team-badge-right">{{ (selectedPeriod === 2) !== isSwapped ? '主队' : '客队' }}</div>
            <div class="field" 
              ref="homeFieldRef" 
              @mousedown="(e) => handleMouseDown(e, 1)"
              @mousemove="(e) => handleMouseMove(e, 1)"
              @touchstart="(e) => handleTouchStart(e, 1)">
              <!-- 热力图容器 -->
              <div ref="homeHeatmapContainer" class="heatmap-container-inner"></div>
              <!-- 圆形光标（仅在热力图模式下显示） -->
              <div 
                v-if="clickStyle === 'heat' && mousePosition && currentActiveTeam === 1"
                class="brush-cursor"
                :style="{
                  left: mousePosition.x + '%',
                  top: mousePosition.y + '%',
                  width: brushSize + 'px',
                  height: brushSize + 'px',
                  marginLeft: '-' + (brushSize / 2) + 'px',
                  marginTop: '-' + (brushSize / 2) + 'px'
                }">
              </div>
              <!-- 足球图标容器 -->
              <div class="football-icons-container">
                <div 
                  v-for="(football, index) in displayHomeFootballIcons" 
                  :key="index"
                  class="football-icon"
                  :style="{
                    left: football.x + '%',
                    top: football.y + '%',
                    transform: 'translate(-50%, -50%)'
                  }">
                  <img :src="qiuIcon" alt="football" class="football-icon-img" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 客队热力图（右侧） -->
        <div class="field-wrapper">
          <div class="field-label">客队</div>
          <div class="field-container-small">
            <!-- 主客标识（在热图外面） -->
            <div class="team-badge team-badge-left">{{ (selectedPeriod === 2) !== isSwapped ? '客队' : '主队' }}</div>
            <div class="team-badge team-badge-right">{{ (selectedPeriod === 2) !== isSwapped ? '主队' : '客队' }}</div>
            <div class="field" 
              ref="awayFieldRef" 
              @mousedown="(e) => handleMouseDown(e, 2)"
              @mousemove="(e) => handleMouseMove(e, 2)"
              @touchstart="(e) => handleTouchStart(e, 2)">
              <!-- 热力图容器 -->
              <div ref="awayHeatmapContainer" class="heatmap-container-inner"></div>
              <!-- 圆形光标（仅在热力图模式下显示） -->
              <div 
                v-if="clickStyle === 'heat' && mousePosition && currentActiveTeam === 2"
                class="brush-cursor"
                :style="{
                  left: mousePosition.x + '%',
                  top: mousePosition.y + '%',
                  width: brushSize + 'px',
                  height: brushSize + 'px',
                  marginLeft: '-' + (brushSize / 2) + 'px',
                  marginTop: '-' + (brushSize / 2) + 'px'
                }">
              </div>
              <!-- 足球图标容器 -->
              <div class="football-icons-container">
                <div 
                  v-for="(football, index) in displayAwayFootballIcons" 
                  :key="index"
                  class="football-icon"
                  :style="{
                    left: football.x + '%',
                    top: football.y + '%',
                    transform: 'translate(-50%, -50%)'
                  }">
                  <img :src="qiuIcon" alt="football" class="football-icon-img" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="control-panel">



        <!-- 点击样式选择图标（悬浮在左侧） -->
        <div class="style-selector">
          <button 
            class="style-btn" 
            :class="{ active: clickStyle === 'heat' }"
            @click="clickStyle = 'heat'"
            title="热点">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M12 2C8 2 5 5 5 9C5 13 8 16 12 20C16 16 19 13 19 9C19 5 16 2 12 2Z" 
                :fill="clickStyle === 'heat' ? '#fda033' : '#999'" 
                :stroke="clickStyle === 'heat' ? '#fda033' : '#999'" 
                stroke-width="1.5"/>
              <circle cx="12" cy="9" r="2.5" fill="white" opacity="0.8"/>
            </svg>
          </button>
          <button 
            class="style-btn" 
            :class="{ active: clickStyle === 'football' }"
            @click="clickStyle = 'football'"
            title="足球">
            <img :src="qiuIcon" alt="football" class="style-icon" />
          </button>
        </div>

        <div class="actions">
          <!-- 左侧：主队的一套 -->
          <div class="actions-left">
            <button class="btn btn-primary" @click="generateHomeHeatmap" :disabled="isUploadingHome || !homeTeamPoints.length">
              {{ isUploadingHome ? '上传中...' : '生成并上传主队' }}
            </button>
            <button class="btn btn-secondary" @click="undo(1)" :disabled="!canUndoHome">
              撤回主队
            </button>
          </div>
          
          <!-- 中间：清除、选择时段、换边 -->
          <div class="actions-center">
            <button class="btn btn-secondary" @click="clearHeatmap">
              清除
            </button>
            <div class="team-select">
              <label>选择时段：</label>
              <select v-model="selectedPeriod" class="team-select-dropdown">
                <option :value="1">半场</option>
                <option :value="2">全场</option>
              </select>
            </div>
            <label class="swap-toggle">
              <input type="checkbox" v-model="isSwapped" />
              <span class="swap-toggle-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                换边
              </span>
            </label>
          </div>
          
          <!-- 右侧：客队的一套 -->
          <div class="actions-right">
            <button class="btn btn-primary" @click="generateAwayHeatmap" :disabled="isUploadingAway || !awayTeamPoints.length">
              {{ isUploadingAway ? '上传中...' : '生成并上传客队' }}
            </button>
            <button class="btn btn-secondary" @click="undo(2)" :disabled="!canUndoAway">
              撤回客队
            </button>
          </div>
          
          <div class="upload-section" style="display: none;">
            <div class="team-select">
              <label>选择时段：</label>
              <select v-model="selectedPeriod" class="team-select-dropdown">
                <option :value="1">半场</option>
                <option :value="2">全场</option>
              </select>
            </div>
            <label class="swap-toggle">
              <input type="checkbox" v-model="isSwapped" />
              <span class="swap-toggle-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                换边
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- 历史记录列表（悬浮在右上方） -->
      <!-- <div class="history-section" v-if="historyRecords.length > 0">
        <h4 class="history-title">历史记录</h4>
        <div class="history-list">
          <div 
            v-for="(record, index) in historyRecords" 
            :key="index"
            class="history-item"
            :class="{ active: currentHistoryIndex === index }"
            @click="loadHistoryRecord(index)">
            <div class="history-info">
              <span class="history-team">{{ record.team === 1 ? '主队' : '客队' }}</span>
              <span class="history-period">{{ record.period === 1 ? '半场' : '全场' }}</span>
              <span class="history-time">{{ formatTime(record.timestamp) }}</span>
            </div>
            <button class="btn-delete" @click.stop="deleteHistoryRecord(index)" title="删除">×</button>
          </div>
        </div>
      </div> -->
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect, watch, nextTick, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import heatmap from 'heatmap.js'
import zqcBg from '@/assets/zqc.png'
import qiuIcon from '@/assets/qiu.png'

export default {
  name: 'MatchHeatmapView',
  setup() {
    const route = useRoute()
    const homeFieldRef = ref(null)
    const awayFieldRef = ref(null)
    const homeHeatmapContainer = ref(null)
    const awayHeatmapContainer = ref(null)
    const homeTeamPoints = ref([]) // 主队热力图数据
    const awayTeamPoints = ref([]) // 客队热力图数据
    const homeFootballIcons = ref([]) // 主队足球图标位置数组
    const awayFootballIcons = ref([]) // 客队足球图标位置数组
    const backgroundImage = ref(zqcBg)
    const isUploadingHome = ref(false)
    const isUploadingAway = ref(false)
    const selectedPeriod = ref(1) // 1: 半场, 2: 全场
    const matchId = ref('') // 比赛ID
    const historyRecords = ref([]) // 历史记录列表
    const currentHistoryIndex = ref(-1) // 当前选中的历史记录索引
    const hasRouteMatchId = ref(false) // 是否有路由参数中的 match_id
    const brushSize = ref(10) // 笔刷大小（像素）
    const isSwapped = ref(false) // 是否换边（主客队位置互换）
    const heatIntensity = ref(0.5) // 热度级别：0.5=低, 1=中, 2=高, 3=很高（默认：低）
    const clickStyle = ref('heat') // 点击样式：'heat'=热点, 'football'=足球
    const mousePosition = ref(null) // 鼠标位置（百分比坐标）
    const currentActiveTeam = ref(null) // 当前活动的队伍（1: 主队, 2: 客队）
    const lastDrawTime = ref(null) // 上次绘制的时间
    const homePositionClickCount = ref(new Map()) // 主队每个位置的点击次数
    const awayPositionClickCount = ref(new Map()) // 客队每个位置的点击次数
    const homePositionStayTime = ref(new Map()) // 主队每个位置的停留开始时间
    const awayPositionStayTime = ref(new Map()) // 客队每个位置的停留开始时间
    const homeHistory = ref([]) // 主队操作历史记录
    const awayHistory = ref([]) // 客队操作历史记录
    const maxHistorySize = 50 // 最大历史记录数
    let homeHeatmapInstance = null
    let awayHeatmapInstance = null
    let footballIconImage = null // 预加载的足球图标图片

    // 计算平均强度（主队）
    const homeAverageIntensity = computed(() => {
      if (homeTeamPoints.value.length === 0) return 0
      const sum = homeTeamPoints.value.reduce((acc, point) => acc + (point.value || 1), 0)
      return sum / homeTeamPoints.value.length
    })

    // 计算平均强度（客队）
    const awayAverageIntensity = computed(() => {
      if (awayTeamPoints.value.length === 0) return 0
      const sum = awayTeamPoints.value.reduce((acc, point) => acc + (point.value || 1), 0)
      return sum / awayTeamPoints.value.length
    })

    // 是否可以撤回（主队）
    const canUndoHome = computed(() => {
      return homeHistory.value.length > 0
    })

    // 是否可以撤回（客队）
    const canUndoAway = computed(() => {
      return awayHistory.value.length > 0
    })

    // 主队显示的足球图标（坐标已经在点击时反转了，这里直接使用）
    const displayHomeFootballIcons = computed(() => {
      return homeFootballIcons.value
    })

    // 客队显示的足球图标（坐标已经在点击时反转了，这里直接使用）
    const displayAwayFootballIcons = computed(() => {
      return awayFootballIcons.value
    })

    // 保存当前状态到历史记录（根据队伍）
    const saveToHistory = (team) => {
      const history = team === 1 ? homeHistory : awayHistory
      const points = team === 1 ? homeTeamPoints : awayTeamPoints
      const icons = team === 1 ? homeFootballIcons : awayFootballIcons
      
      const state = {
        heatmapPoints: JSON.parse(JSON.stringify(points.value)),
        footballIcons: JSON.parse(JSON.stringify(icons.value)),
        timestamp: Date.now()
      }
      
      // 检查是否与上一个状态相同（避免重复保存）
      if (history.value.length > 0) {
        const lastState = history.value[history.value.length - 1]
        const pointsSame = JSON.stringify(lastState.heatmapPoints) === JSON.stringify(state.heatmapPoints)
        const iconsSame = JSON.stringify(lastState.footballIcons) === JSON.stringify(state.footballIcons)
        if (pointsSame && iconsSame) {
          // 状态相同，不保存
          return
        }
      }
      
      history.value.push(state)
      // 限制历史记录数量
      if (history.value.length > maxHistorySize) {
        history.value.shift()
      }
    }

    // 撤回操作（根据队伍）
    const undo = (team) => {
      const history = team === 1 ? homeHistory : awayHistory
      const points = team === 1 ? homeTeamPoints : awayTeamPoints
      const icons = team === 1 ? homeFootballIcons : awayFootballIcons
      const clickCount = team === 1 ? homePositionClickCount : awayPositionClickCount
      const stayTime = team === 1 ? homePositionStayTime : awayPositionStayTime
      
      if (history.value.length === 0) return
      
      // 移除最后一个历史记录（当前状态）
      history.value.pop()
      
      // 恢复到上一个状态
      if (history.value.length > 0) {
        const previousState = history.value[history.value.length - 1]
        points.value = JSON.parse(JSON.stringify(previousState.heatmapPoints))
        icons.value = JSON.parse(JSON.stringify(previousState.footballIcons))
      } else {
        // 如果没有历史记录，清空所有
        points.value = []
        icons.value = []
      }
      
      // 清除点击计数和停留记录
      clickCount.value.clear()
      stayTime.value.clear()
      lastDrawTime.value = null
      
      // 更新热力图
      nextTick(() => {
        if (team === 1) {
          updateHomeHeatmap()
        } else {
          updateAwayHeatmap()
        }
      })
      
      // 保存数据
      saveData()
    }

    // 初始化主队热力图
    const initHomeHeatmap = () => {
      if (!homeHeatmapContainer.value || !homeFieldRef.value) {
        console.warn('主队容器引用不存在')
        return
      }

      const rect = homeFieldRef.value.getBoundingClientRect()
      let width = Math.max(rect.width, 1)
      let height = Math.max(rect.height, 1)

      if (width <= 0 || height <= 0 || !isFinite(width) || !isFinite(height)) {
        console.warn('主队容器尺寸无效，无法初始化热力图', { width, height })
        return
      }

      if (!homeHeatmapContainer.value.offsetParent && homeHeatmapContainer.value.style.display !== 'none') {
        console.warn('主队容器不在 DOM 中或不可见')
        return
      }

      if (homeHeatmapInstance) {
        try {
          const oldCanvas = homeHeatmapContainer.value.querySelector('canvas')
          if (oldCanvas) {
            oldCanvas.remove()
          }
          homeHeatmapInstance = null
        } catch (e) {
          console.warn('清理主队热力图实例时出错', e)
        }
      }

      const isLargeScreen = width > 600
      const baseRadius = isLargeScreen ? 50 : 30
      const maxRadius = Math.min(width, height) / 6
      const radius = Math.max(15, Math.min(baseRadius, maxRadius))
      const blur = Math.max(isLargeScreen ? 0.7 : 0.5, 0.1)

      try {
        if (!homeHeatmapContainer.value.style.width) {
          homeHeatmapContainer.value.style.width = '100%'
        }
        if (!homeHeatmapContainer.value.style.height) {
          homeHeatmapContainer.value.style.height = '100%'
        }

        // 半径直接基于笔刷大小，最小值为8
        const dynamicRadius = Math.max(8, brushSize.value * 1.2)
        
        homeHeatmapInstance = heatmap.create({
          container: homeHeatmapContainer.value,
          maxOpacity: 0.8,
          minOpacity: 0.1,
          blur: Math.max(blur * 0.85, 0.3),
          radius: dynamicRadius,
          gradient: {
            0.0: 'rgba(255,255,200,0)',
            0.25: 'rgba(255,255,200,0.3)',
            0.4: 'rgb(255,255,150)',
            0.55: 'rgb(255,255,100)',
            0.7: 'rgb(255,220,50)',
            0.82: 'rgb(255,180,0)',
            0.9: 'rgb(255,120,0)',
            0.95: 'rgb(255,60,0)',
            1.0: 'rgb(255,0,0)'
          }
        })

        console.log('主队热力图初始化成功', { width, height, radius, blur })
      } catch (error) {
        console.error('创建主队热力图实例失败', error, { width, height, radius, blur })
        homeHeatmapInstance = null
      }
    }

    // 初始化客队热力图
    const initAwayHeatmap = () => {
      if (!awayHeatmapContainer.value || !awayFieldRef.value) {
        console.warn('客队容器引用不存在')
        return
      }

      const rect = awayFieldRef.value.getBoundingClientRect()
      let width = Math.max(rect.width, 1)
      let height = Math.max(rect.height, 1)

      if (width <= 0 || height <= 0 || !isFinite(width) || !isFinite(height)) {
        console.warn('客队容器尺寸无效，无法初始化热力图', { width, height })
        return
      }

      if (!awayHeatmapContainer.value.offsetParent && awayHeatmapContainer.value.style.display !== 'none') {
        console.warn('客队容器不在 DOM 中或不可见')
        return
      }

      if (awayHeatmapInstance) {
        try {
          const oldCanvas = awayHeatmapContainer.value.querySelector('canvas')
          if (oldCanvas) {
            oldCanvas.remove()
          }
          awayHeatmapInstance = null
        } catch (e) {
          console.warn('清理客队热力图实例时出错', e)
        }
      }

      const isLargeScreen = width > 600
      const baseRadius = isLargeScreen ? 50 : 30
      const maxRadius = Math.min(width, height) / 6
      const radius = Math.max(15, Math.min(baseRadius, maxRadius))
      const blur = Math.max(isLargeScreen ? 0.7 : 0.5, 0.1)

      try {
        if (!awayHeatmapContainer.value.style.width) {
          awayHeatmapContainer.value.style.width = '100%'
        }
        if (!awayHeatmapContainer.value.style.height) {
          awayHeatmapContainer.value.style.height = '100%'
        }

        // 半径直接基于笔刷大小，最小值为8
        const dynamicRadius = Math.max(8, brushSize.value * 1.2)
        
        awayHeatmapInstance = heatmap.create({
          container: awayHeatmapContainer.value,
          maxOpacity: 0.8,
          minOpacity: 0.1,
          blur: Math.max(blur * 0.85, 0.3),
          radius: dynamicRadius,
          gradient: {
            0.0: 'rgba(255,255,200,0)',
            0.25: 'rgba(255,255,200,0.3)',
            0.4: 'rgb(255,255,150)',
            0.55: 'rgb(255,255,100)',
            0.7: 'rgb(255,220,50)',
            0.82: 'rgb(255,180,0)',
            0.9: 'rgb(255,120,0)',
            0.95: 'rgb(255,60,0)',
            1.0: 'rgb(255,0,0)'
          }
        })

        console.log('客队热力图初始化成功', { width, height, radius, blur })
      } catch (error) {
        console.error('创建客队热力图实例失败', error, { width, height, radius, blur })
        awayHeatmapInstance = null
      }
    }


    // 更新主队热力图渲染（左侧显示）
    const updateHomeHeatmap = () => {
      if (!homeFieldRef.value || !homeHeatmapContainer.value) {
        console.warn('主队容器不存在，无法更新热力图')
        return
      }

      const rect = homeFieldRef.value.getBoundingClientRect()
      const width = Math.max(rect.width, 1)
      const height = Math.max(rect.height, 1)

      if (width <= 0 || height <= 0 || !isFinite(width) || !isFinite(height)) {
        console.warn('主队容器尺寸无效，无法更新热力图', { width, height })
        return
      }

      if (!homeHeatmapInstance) {
        console.log('主队热力图实例不存在，初始化中...')
        initHomeHeatmap()
        if (!homeHeatmapInstance) {
          console.error('初始化主队热力图失败')
          return
        }
      }

      const containerRect = homeHeatmapContainer.value.getBoundingClientRect()
      const sizeChanged = Math.abs(containerRect.width - width) > 1 || Math.abs(containerRect.height - height) > 1
      
      let currentRadius = 30
      if (homeHeatmapInstance && homeHeatmapInstance._config) {
        currentRadius = homeHeatmapInstance._config.radius || 30
      }
      // 半径应该基于笔刷大小，最小值为8，最大不超过容器的1/6
      const expectedRadius = Math.max(8, Math.min(width, height) / 6, brushSize.value * 1.2)
      const radiusChanged = Math.abs(currentRadius - expectedRadius) > 3
      
      if (sizeChanged || radiusChanged) {
        console.log('主队容器尺寸或笔刷大小变化，重新初始化热力图')
        initHomeHeatmap()
        if (!homeHeatmapInstance) {
          console.error('重新初始化主队热力图失败')
          return
        }
      }

      if (homeTeamPoints.value.length === 0) {
        try {
          homeHeatmapInstance.setData({ max: 0.1, data: [] })
        } catch (error) {
          console.error('清空主队热力图失败', error)
          initHomeHeatmap()
        }
        return
      }

      const maxValue = Math.max(...homeTeamPoints.value.map(p => Math.max(p.value || 0.1, 0.1)), 0.1)
      
      if (homeHeatmapInstance && homeHeatmapInstance._config) {
        currentRadius = homeHeatmapInstance._config.radius || 30
      }
      
      // 坐标已经在点击时反转了，这里直接使用
      const pointsToUse = homeTeamPoints.value
      
      const margin = Math.max(currentRadius, 15)
      const validData = pointsToUse
        .map(point => {
          let x = Math.round((point.x / 100) * width)
          let y = Math.round((point.y / 100) * height)
          const value = Math.max(point.value || 0.1, 0.1)
          
          x = Math.max(margin, Math.min(x, width - margin))
          y = Math.max(margin, Math.min(y, height - margin))
          
          if (!isFinite(x) || !isFinite(y) || !isFinite(value) || x < 0 || y < 0) {
            return null
          }
          
          return { x, y, value }
        })
        .filter(point => point !== null)

      if (validData.length === 0) {
        console.warn('主队没有有效的数据点')
        return
      }

      const data = {
        max: Math.max(maxValue, 0.1),
        data: validData
      }

      try {
        homeHeatmapInstance.setData(data)
      } catch (error) {
        console.error('更新主队热力图失败', error, { width, height, dataLength: validData.length })
        console.log('尝试重新初始化主队热力图...')
        initHomeHeatmap()
        if (homeHeatmapInstance) {
          try {
            homeHeatmapInstance.setData(data)
          } catch (retryError) {
            console.error('重试更新主队热力图仍然失败', retryError)
          }
        }
      }
    }

    // 更新客队热力图渲染（右侧显示）
    const updateAwayHeatmap = () => {
      if (!awayFieldRef.value || !awayHeatmapContainer.value) {
        console.warn('客队容器不存在，无法更新热力图')
        return
      }

      const rect = awayFieldRef.value.getBoundingClientRect()
      const width = Math.max(rect.width, 1)
      const height = Math.max(rect.height, 1)

      if (width <= 0 || height <= 0 || !isFinite(width) || !isFinite(height)) {
        console.warn('客队容器尺寸无效，无法更新热力图', { width, height })
        return
      }

      if (!awayHeatmapInstance) {
        console.log('客队热力图实例不存在，初始化中...')
        initAwayHeatmap()
        if (!awayHeatmapInstance) {
          console.error('初始化客队热力图失败')
          return
        }
      }

      const containerRect = awayHeatmapContainer.value.getBoundingClientRect()
      const sizeChanged = Math.abs(containerRect.width - width) > 1 || Math.abs(containerRect.height - height) > 1
      
      let currentRadius = 30
      if (awayHeatmapInstance && awayHeatmapInstance._config) {
        currentRadius = awayHeatmapInstance._config.radius || 30
      }
      // 半径应该基于笔刷大小，最小值为8，最大不超过容器的1/6
      const expectedRadius = Math.max(8, Math.min(width, height) / 6, brushSize.value * 1.2)
      const radiusChanged = Math.abs(currentRadius - expectedRadius) > 3
      
      if (sizeChanged || radiusChanged) {
        console.log('客队容器尺寸或笔刷大小变化，重新初始化热力图')
        initAwayHeatmap()
        if (!awayHeatmapInstance) {
          console.error('重新初始化客队热力图失败')
          return
        }
      }

      if (awayTeamPoints.value.length === 0) {
        try {
          awayHeatmapInstance.setData({ max: 0.1, data: [] })
        } catch (error) {
          console.error('清空客队热力图失败', error)
          initAwayHeatmap()
        }
        return
      }

      const maxValue = Math.max(...awayTeamPoints.value.map(p => Math.max(p.value || 0.1, 0.1)), 0.1)
      
      if (awayHeatmapInstance && awayHeatmapInstance._config) {
        currentRadius = awayHeatmapInstance._config.radius || 30
      }
      
      // 坐标已经在点击时反转了，这里直接使用
      const pointsToUse = awayTeamPoints.value
      
      const margin = Math.max(currentRadius, 15)
      const validData = pointsToUse
        .map(point => {
          let x = Math.round((point.x / 100) * width)
          let y = Math.round((point.y / 100) * height)
          const value = Math.max(point.value || 0.1, 0.1)
          
          x = Math.max(margin, Math.min(x, width - margin))
          y = Math.max(margin, Math.min(y, height - margin))
          
          if (!isFinite(x) || !isFinite(y) || !isFinite(value) || x < 0 || y < 0) {
            return null
          }
          
          return { x, y, value }
        })
        .filter(point => point !== null)

      if (validData.length === 0) {
        console.warn('客队没有有效的数据点')
        return
      }

      const data = {
        max: Math.max(maxValue, 0.1),
        data: validData
      }

      try {
        awayHeatmapInstance.setData(data)
      } catch (error) {
        console.error('更新客队热力图失败', error, { width, height, dataLength: validData.length })
        console.log('尝试重新初始化客队热力图...')
        initAwayHeatmap()
        if (awayHeatmapInstance) {
          try {
            awayHeatmapInstance.setData(data)
          } catch (retryError) {
            console.error('重试更新客队热力图仍然失败', retryError)
          }
        }
      }
    }

    // 查找附近是否已有热点（在指定范围内，根据队伍）
    const findNearbyPoints = (x, y, team, threshold = 2) => {
      const points = team === 1 ? homeTeamPoints : awayTeamPoints
      return points.value.filter(point => {
        const distance = Math.sqrt(
          Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2)
        )
        return distance <= threshold
      })
    }


    // 增强已有热点的强度（根据队伍）
    const enhanceNearbyPoints = (x, y, team, enhanceValue) => {
      const nearbyPoints = findNearbyPoints(x, y, team, 2)
      if (nearbyPoints.length > 0) {
        nearbyPoints.forEach(point => {
          const distance = Math.sqrt(
            Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2)
          )
          const distanceFactor = Math.max(0, 1 - distance / 2)
          point.value = Math.min(3.0, (point.value || 0.1) + enhanceValue * distanceFactor)
        })
        return true
      }
      return false
    }

    // 添加热力点（根据笔刷大小添加多个点形成圆形，根据队伍）
    const addHeatPoint = (x, y, team) => {
      const fieldRef = team === 1 ? homeFieldRef : awayFieldRef
      const heatmapInstance = team === 1 ? homeHeatmapInstance : awayHeatmapInstance
      const points = team === 1 ? homeTeamPoints : awayTeamPoints
      const clickCount = team === 1 ? homePositionClickCount : awayPositionClickCount
      const updateFn = team === 1 ? updateHomeHeatmap : updateAwayHeatmap
      
      if (!fieldRef.value || !heatmapInstance) return false

      const rect = fieldRef.value.getBoundingClientRect()
      const width = Math.max(rect.width, 1)
      const height = Math.max(rect.height, 1)

      if (width <= 0 || height <= 0) {
        return false
      }

      if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
        const now = Date.now()
        const positionKey = `${Math.round(x * 10) / 10}_${Math.round(y * 10) / 10}`

        const currentClickCount = clickCount.value.get(positionKey) || 0
        
        // 如果该位置已经有点，增强附近点的强度
        if (currentClickCount > 0) {
          const enhanceValue = 0.15
          if (enhanceNearbyPoints(x, y, team, enhanceValue)) {
            clickCount.value.set(positionKey, currentClickCount + 1)
            lastDrawTime.value = now
            
            nextTick(() => {
              updateFn()
            })
            return true
          }
        }

        const pixelX = Math.round((x / 100) * width)
        const pixelY = Math.round((y / 100) * height)
        
        // 根据笔刷大小计算点数，brushSize=10时，pointsToAdd=2，但最少为2
        const pointsToAdd = Math.max(2, Math.floor(brushSize.value / 5))
        
        const clickIntensityMap = {
          0.5: { min: 0.05, max: 0.15 },
          1: { min: 0.1, max: 0.2 },
          2: { min: 0.15, max: 0.25 },
          3: { min: 0.2, max: 0.3 }
        }
        const intensityMap = clickIntensityMap
        const intensityRange = intensityMap[heatIntensity.value] || clickIntensityMap[1]
        const baseIntensity = intensityRange.min
        const intensitySpread = intensityRange.max - intensityRange.min
        
        const newPoints = []
        
        for (let i = 0; i < pointsToAdd; i++) {
          const angle = (Math.PI * 2 * i) / pointsToAdd
          // 根据笔刷大小调整半径，brushSize=10时，半径约为2-3像素
          const radiusPixels = (brushSize.value * 0.2) * (0.5 + Math.random() * 0.5)
          const distanceFromCenter = radiusPixels / (brushSize.value * 0.2)
          const intensityMultiplier = 1 - distanceFromCenter * 0.3
          const offsetX = Math.cos(angle) * radiusPixels
          const offsetY = Math.sin(angle) * radiusPixels
          
          const pointX = Math.max(0, Math.min(width - 1, pixelX + offsetX))
          const pointY = Math.max(0, Math.min(height - 1, pixelY + offsetY))
          
          const value = (baseIntensity + Math.random() * intensitySpread) * intensityMultiplier
          
          newPoints.push({
            x: (pointX / width) * 100,
            y: (pointY / height) * 100,
            value: value
          })
        }
        
        points.value.push(...newPoints)
        
        clickCount.value.set(positionKey, 1)
        lastDrawTime.value = now
        
        nextTick(() => {
          updateFn()
        })

        return true
      }
      return false
    }

    // 获取坐标（百分比，根据队伍）
    const getCoordinates = (clientX, clientY, team) => {
      const fieldRef = team === 1 ? homeFieldRef : awayFieldRef
      if (!fieldRef.value) return null

      const rect = fieldRef.value.getBoundingClientRect()
      const width = Math.max(rect.width, 1)
      const height = Math.max(rect.height, 1)

      if (width <= 0 || height <= 0) {
        return null
      }

      const x = ((clientX - rect.left) / width) * 100
      const y = ((clientY - rect.top) / height) * 100

      return { x, y }
    }

    // 处理鼠标按下（根据队伍）
    const handleMouseDown = (event, team) => {
      const fieldRef = team === 1 ? homeFieldRef : awayFieldRef
      if (!fieldRef.value) return
      
      event.preventDefault()
      event.stopPropagation()
      
      currentActiveTeam.value = team
      
      if (currentHistoryIndex.value >= 0) {
        currentHistoryIndex.value = -1
      }
      
      saveToHistory(team)
      
      const coords = getCoordinates(event.clientX, event.clientY, team)
      if (coords) {
        if (clickStyle.value === 'football') {
          const icons = team === 1 ? homeFootballIcons : awayFootballIcons
          icons.value.push({
            x: coords.x,
            y: coords.y
          })
          saveData()
        } else {
          const heatmapInstance = team === 1 ? homeHeatmapInstance : awayHeatmapInstance
          const initFn = team === 1 ? initHomeHeatmap : initAwayHeatmap
          if (!heatmapInstance) {
            initFn()
          }
          if (!heatmapInstance) {
            console.warn('热力图实例初始化失败')
            return
          }
          addHeatPoint(coords.x, coords.y, team)
        }
      }
    }

    // 处理鼠标移动（仅显示圆形光标，不拖动绘制）
    const handleMouseMove = (event, team) => {
      const fieldRef = team === 1 ? homeFieldRef : awayFieldRef
      if (!fieldRef.value) return

      const coords = getCoordinates(event.clientX, event.clientY, team)

      if (clickStyle.value === 'heat') {
        if (coords) {
          mousePosition.value = coords
          currentActiveTeam.value = team
        } else {
          mousePosition.value = null
        }
      }
    }

    // 处理触摸开始（根据队伍）
    const handleTouchStart = (event, team) => {
      const fieldRef = team === 1 ? homeFieldRef : awayFieldRef
      if (!fieldRef.value) return
      
      event.preventDefault()
      event.stopPropagation()
      
      currentActiveTeam.value = team
      
      if (currentHistoryIndex.value >= 0) {
        currentHistoryIndex.value = -1
      }
      
      saveToHistory(team)
      
      const touch = event.touches[0]
      const coords = getCoordinates(touch.clientX, touch.clientY, team)
      if (coords) {
        if (clickStyle.value === 'football') {
          const icons = team === 1 ? homeFootballIcons : awayFootballIcons
          icons.value.push({
            x: coords.x,
            y: coords.y
          })
          saveData()
        } else {
          const heatmapInstance = team === 1 ? homeHeatmapInstance : awayHeatmapInstance
          const initFn = team === 1 ? initHomeHeatmap : initAwayHeatmap
          if (!heatmapInstance) {
            initFn()
          }
          if (!heatmapInstance) {
            console.warn('热力图实例初始化失败')
            return
          }
          addHeatPoint(coords.x, coords.y, team)
          lastDrawTime.value = Date.now()
        }
      }
    }


    // 获取热度级别标签
    const getHeatIntensityLabel = (value) => {
      const labels = {
        0.5: '低',
        1: '中',
        2: '高',
        3: '很高'
      }
      return labels[value] || '中'
    }


    // 上传单个队伍的热力图
    const uploadTeamHeatmap = async (team) => {
      // team: 1=主队, 2=客队
      const fieldRef = team === 1 ? homeFieldRef : awayFieldRef
      const heatmapContainer = team === 1 ? homeHeatmapContainer : awayHeatmapContainer
      const points = team === 1 ? homeTeamPoints : awayTeamPoints
      const icons = team === 1 ? displayHomeFootballIcons : displayAwayFootballIcons
      
      if (points.value.length === 0) {
        return Promise.resolve() // 如果没有数据，跳过上传
      }

      const rect = fieldRef.value.getBoundingClientRect()
      const width = Math.max(rect.width, 1)
      const height = Math.max(rect.height, 1)

      if (width <= 0 || height <= 0) {
        throw new Error('容器尺寸无效')
      }

      const canvas = heatmapContainer.value.querySelector('canvas')
      if (!canvas) {
        throw new Error('无法找到热力图 canvas')
      }

      const finalCanvas = document.createElement('canvas')
      const ctx = finalCanvas.getContext('2d')
      
      finalCanvas.width = width
      finalCanvas.height = height

      const bgImage = new Image()
      bgImage.crossOrigin = 'anonymous'
      bgImage.src = backgroundImage.value

      return new Promise((resolve, reject) => {
        bgImage.onload = () => {
          try {
            const imgAspect = 622 / 341
            const canvasAspect = width / height
            let drawWidth = width
            let drawHeight = height
            let offsetX = 0
            let offsetY = 0

            if (canvasAspect > imgAspect) {
              drawHeight = height
              drawWidth = height * imgAspect
              offsetX = (width - drawWidth) / 2
            } else {
              drawWidth = width
              drawHeight = width / imgAspect
              offsetY = (height - drawHeight) / 2
            }

            ctx.fillStyle = '#2d5016'
            ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height)
            ctx.drawImage(bgImage, offsetX, offsetY, drawWidth, drawHeight)
            ctx.drawImage(canvas, 0, 0)

            drawFootballIcons(ctx, icons.value, width, height).then(() => {
              finalCanvas.toBlob((blob) => {
                if (!blob) {
                  reject(new Error('生成图片失败'))
                  return
                }

                const formData = new FormData()
                formData.append('file', blob, `heatmap-${team}-${Date.now()}.png`)

                fetch('https://api.zctj16888.com/app/v1/file', {
                  method: 'POST',
                  body: formData
                })
                  .then(response => {
                    if (!response.ok) {
                      throw new Error(`上传失败: ${response.statusText}`)
                    }
                    return response.json()
                  })
                  .then(result => {
                    if (result.code === 1 && result.data && result.data.url) {
                      const imageUrl = result.data.url
                      return uploadHeatmapInfo(imageUrl, team)
                    } else {
                      throw new Error('未获取到图片URL，返回数据: ' + JSON.stringify(result))
                    }
                  })
                  .then(() => {
                    resolve()
                  })
                  .catch(reject)
              }, 'image/png')
            })
          } catch (error) {
            reject(error)
          }
        }

        bgImage.onerror = () => {
          ctx.fillStyle = '#2d5016'
          ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height)
          ctx.drawImage(canvas, 0, 0)

          drawFootballIcons(ctx, icons.value, width, height).then(() => {
            finalCanvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('生成图片失败'))
                return
              }

              const formData = new FormData()
              formData.append('file', blob, `heatmap-${team}-${Date.now()}.png`)

              fetch('https://api.zctj16888.com/app/v1/file', {
                method: 'POST',
                body: formData
              })
                .then(response => {
                  if (!response.ok) {
                    throw new Error(`上传失败: ${response.statusText}`)
                  }
                  return response.json()
                })
                .then(result => {
                  if (result.code === 1 && result.data && result.data.url) {
                    const imageUrl = result.data.url
                    return uploadHeatmapInfo(imageUrl, team)
                  } else {
                    throw new Error('未获取到图片URL，返回数据: ' + JSON.stringify(result))
                  }
                })
                .then(() => {
                  resolve()
                })
                .catch(reject)
            }, 'image/png')
          })
        }
      })
    }

    // 生成并上传主队热力图
    const generateHomeHeatmap = async () => {
      if (homeTeamPoints.value.length === 0) {
        alert('请先在主队球场上点击生成热力图数据')
        return
      }

      if (!matchId.value || matchId.value.trim() === '') {
        alert('请先输入比赛ID')
        return
      }

      isUploadingHome.value = true

      try {
        await uploadTeamHeatmap(1)
        saveHistoryRecord(1)
        alert('主队上传成功！')
      } catch (error) {
        console.error('主队上传失败:', error)
        alert('主队上传失败: ' + error.message)
      } finally {
        isUploadingHome.value = false
      }
    }

    // 生成并上传客队热力图
    const generateAwayHeatmap = async () => {
      if (awayTeamPoints.value.length === 0) {
        alert('请先在客队球场上点击生成热力图数据')
        return
      }

      if (!matchId.value || matchId.value.trim() === '') {
        alert('请先输入比赛ID')
        return
      }

      isUploadingAway.value = true

      try {
        await uploadTeamHeatmap(2)
        saveHistoryRecord(2)
        alert('客队上传成功！')
      } catch (error) {
        console.error('客队上传失败:', error)
        alert('客队上传失败: ' + error.message)
      } finally {
        isUploadingAway.value = false
      }
    }

    // 预加载足球图标图片
    const preloadFootballIcon = () => {
      return new Promise((resolve, reject) => {
        if (footballIconImage && footballIconImage.complete) {
          resolve(footballIconImage)
          return
        }
        
        footballIconImage = new Image()
        footballIconImage.crossOrigin = 'anonymous'
        footballIconImage.src = qiuIcon
        
        footballIconImage.onload = () => {
          console.log('足球图标图片加载成功')
          resolve(footballIconImage)
        }
        
        footballIconImage.onerror = (error) => {
          console.error('足球图标图片加载失败:', error, '图片路径:', qiuIcon)
          reject(error)
        }
      })
    }

    // 绘制足球图标到 canvas
    const drawFootballIcons = (ctx, footballIcons, width, height) => {
      return new Promise((resolve) => {
        if (footballIcons.length === 0) {
          resolve()
          return
        }
        
        // 如果图片已加载，直接使用
        if (footballIconImage && footballIconImage.complete) {
          const iconSize = 16 // 图标大小
          const radius = iconSize / 2
          footballIcons.forEach(football => {
            const x = (football.x / 100) * width
            const y = (football.y / 100) * height
            // 使用圆形裁剪绘制图片
            ctx.save()
            ctx.beginPath()
            ctx.arc(x, y, radius, 0, Math.PI * 2)
            ctx.clip()
            ctx.drawImage(footballIconImage, x - radius, y - radius, iconSize, iconSize)
            ctx.restore()
          })
          resolve()
          return
        }

        // 否则尝试加载图片
        preloadFootballIcon()
          .then((img) => {
            const iconSize = 16 // 图标大小
            const radius = iconSize / 2
            footballIcons.forEach(football => {
              const x = (football.x / 100) * width
              const y = (football.y / 100) * height
              // 使用圆形裁剪绘制图片
              ctx.save()
              ctx.beginPath()
              ctx.arc(x, y, radius, 0, Math.PI * 2)
              ctx.clip()
              ctx.drawImage(img, x - radius, y - radius, iconSize, iconSize)
              ctx.restore()
            })
            resolve()
          })
          .catch(() => {
            // 如果图片加载失败，使用 emoji 作为后备
            console.warn('使用 emoji 作为后备方案')
            ctx.font = '16px Arial'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
            ctx.shadowBlur = 3
            ctx.shadowOffsetX = 0
            ctx.shadowOffsetY = 0
            footballIcons.forEach(football => {
              const x = (football.x / 100) * width
              const y = (football.y / 100) * height
              ctx.fillText('⚽', x, y)
            })
            ctx.shadowColor = 'transparent'
            ctx.shadowBlur = 0
            resolve()
          })
      })
    }

    // 监听换边状态变化，保存到本地存储
    watch(isSwapped, (newValue) => {
      try {
        localStorage.setItem('match_heatmap_swapped', String(newValue))
      } catch (e) {
        console.error('保存换边状态失败:', e)
      }
    })

    // 保存 match_id 到本地存储
    const saveMatchId = () => {
      try {
        localStorage.setItem('match_heatmap_match_id', matchId.value)
      } catch (e) {
        console.error('保存 match_id 失败:', e)
      }
    }

    // 上传热力图信息到第二个接口
    const uploadHeatmapInfo = async (imageUrl, team) => {
      if (!matchId.value || matchId.value.trim() === '') {
        throw new Error('请先输入比赛ID')
      }

      try {
        const response = await fetch('https://api.zctj16888.com/app/v1/heatmap', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            match_id: matchId.value.trim(),
            type: team,
            url: imageUrl
          })
        })

        if (!response.ok) {
          throw new Error(`上传热力图信息失败: ${response.statusText}`)
        }

        const result = await response.json()
        if (result.code !== 1) {
          throw new Error(result.msg || '上传失败')
        }

        return result
      } catch (error) {
        console.error('上传热力图信息失败:', error)
        throw error
      }
    }


    // 清除热力图
    const clearHeatmap = () => {
      if (confirm('确定要清除所有热力图数据吗？')) {
        homeTeamPoints.value = []
        awayTeamPoints.value = []
        homeFootballIcons.value = []
        awayFootballIcons.value = []
        homePositionClickCount.value.clear()
        awayPositionClickCount.value.clear()
        homePositionStayTime.value.clear()
        awayPositionStayTime.value.clear()
        homeHistory.value = []
        awayHistory.value = []
        currentHistoryIndex.value = -1
        lastDrawTime.value = null
        
        nextTick(() => {
          if (homeHeatmapInstance) {
            try {
              homeHeatmapInstance.setData({ max: 0.1, data: [] })
              initHomeHeatmap()
            } catch (error) {
              console.error('清空主队热力图失败', error)
              initHomeHeatmap()
            }
          } else {
            initHomeHeatmap()
          }
          
          if (awayHeatmapInstance) {
            try {
              awayHeatmapInstance.setData({ max: 0.1, data: [] })
              initAwayHeatmap()
            } catch (error) {
              console.error('清空客队热力图失败', error)
              initAwayHeatmap()
            }
          } else {
            initAwayHeatmap()
          }
        })
        
        saveData()
      }
    }

    // 下载热力图（下载主队）
    const downloadHeatmap = () => {
      if (!homeFieldRef.value || !homeHeatmapContainer.value) return

      const canvas = homeHeatmapContainer.value.querySelector('canvas')
      if (!canvas) {
        alert('无法找到热力图 canvas')
        return
      }

      const rect = homeFieldRef.value.getBoundingClientRect()
      const width = Math.max(rect.width, 1)
      const height = Math.max(rect.height, 1)

      if (width <= 0 || height <= 0) {
        alert('容器尺寸无效，无法下载')
        return
      }

      try {
        const bgImage = new Image()
        bgImage.crossOrigin = 'anonymous'
        bgImage.src = backgroundImage.value

        bgImage.onload = () => {
          const finalCanvas = document.createElement('canvas')
          const ctx = finalCanvas.getContext('2d')
          
          finalCanvas.width = width
          finalCanvas.height = height

          const imgAspect = 622 / 341
          const canvasAspect = width / height
          let drawWidth = width
          let drawHeight = height
          let offsetX = 0
          let offsetY = 0

          if (canvasAspect > imgAspect) {
            drawHeight = height
            drawWidth = height * imgAspect
            offsetX = (width - drawWidth) / 2
          } else {
            drawWidth = width
            drawHeight = width / imgAspect
            offsetY = (height - drawHeight) / 2
          }

          ctx.fillStyle = '#2d5016'
          ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height)
          ctx.drawImage(bgImage, offsetX, offsetY, drawWidth, drawHeight)
          drawFieldLines(ctx, finalCanvas.width, finalCanvas.height)
          ctx.drawImage(canvas, 0, 0)

          drawFootballIcons(ctx, displayHomeFootballIcons.value, width, height).then(() => {
            finalCanvas.toBlob((blob) => {
            if (!blob) {
              alert('生成图片失败')
              return
            }
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `match-heatmap-home-${Date.now()}.png`
            a.click()
            URL.revokeObjectURL(url)
            }, 'image/png')
          })
        }

        bgImage.onerror = () => {
          const finalCanvas = document.createElement('canvas')
          const ctx = finalCanvas.getContext('2d')
          
          finalCanvas.width = width
          finalCanvas.height = height

          ctx.fillStyle = '#2d5016'
          ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height)
          ctx.drawImage(canvas, 0, 0)

          drawFootballIcons(ctx, displayHomeFootballIcons.value, width, height).then(() => {
            finalCanvas.toBlob((blob) => {
            if (!blob) {
              alert('生成图片失败')
              return
            }
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `match-heatmap-home-${Date.now()}.png`
            a.click()
            URL.revokeObjectURL(url)
          }, 'image/png')
          })
        }
      } catch (error) {
        console.error('下载热力图失败', error)
        alert('下载失败：' + error.message)
      }
    }

    // 绘制球场线条
    const drawFieldLines = (ctx, width, height) => {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)'
      ctx.lineWidth = 2

      // 外框
      ctx.strokeRect(0, 0, width, height)

      // 中线
      ctx.beginPath()
      ctx.moveTo(width / 2, 0)
      ctx.lineTo(width / 2, height)
      ctx.stroke()

      // 中心圆
      ctx.beginPath()
      ctx.arc(width / 2, height / 2, width * 0.1, 0, Math.PI * 2)
      ctx.stroke()

      // 中心点
      ctx.beginPath()
      ctx.arc(width / 2, height / 2, 2, 0, Math.PI * 2)
      ctx.fill()

      // 左半场禁区
      const penaltyWidth = width * 0.15
      const penaltyHeight = height * 0.4
      ctx.strokeRect(0, height / 2 - penaltyHeight / 2, penaltyWidth, penaltyHeight)

      // 左半场小禁区
      const goalWidth = width * 0.08
      const goalHeight = height * 0.2
      ctx.strokeRect(0, height / 2 - goalHeight / 2, goalWidth, goalHeight)

      // 右半场禁区
      ctx.strokeRect(width - penaltyWidth, height / 2 - penaltyHeight / 2, penaltyWidth, penaltyHeight)

      // 右半场小禁区
      ctx.strokeRect(width - goalWidth, height / 2 - goalHeight / 2, goalWidth, goalHeight)

      // 点球点
      ctx.beginPath()
      ctx.arc(penaltyWidth * 0.73, height / 2, 1.5, 0, Math.PI * 2)
      ctx.fill()
      ctx.beginPath()
      ctx.arc(width - penaltyWidth * 0.73, height / 2, 1.5, 0, Math.PI * 2)
      ctx.fill()
    }

    // 保存数据
    const saveData = () => {
      try {
        localStorage.setItem('match_heatmap', JSON.stringify({
          homePoints: homeTeamPoints.value,
          awayPoints: awayTeamPoints.value,
          homeFootballIcons: homeFootballIcons.value,
          awayFootballIcons: awayFootballIcons.value
        }))
      } catch (e) {
        console.error('保存数据失败:', e)
      }
    }

    // 保存历史记录（根据队伍）
    const saveHistoryRecord = (team) => {
      try {
        const points = team === 1 ? homeTeamPoints : awayTeamPoints
        const icons = team === 1 ? homeFootballIcons : awayFootballIcons
        
        const record = {
          team: team,
          period: selectedPeriod.value,
          timestamp: Date.now(),
          heatmapPoints: JSON.parse(JSON.stringify(points.value)),
          footballIcons: JSON.parse(JSON.stringify(icons.value)),
          matchId: matchId.value
        }
        
        const existingIndex = historyRecords.value.findIndex(
          r => r.team === record.team && r.period === record.period && r.matchId === record.matchId
        )
        
        if (existingIndex >= 0) {
          historyRecords.value[existingIndex] = record
        } else {
          historyRecords.value.push(record)
        }
        
        historyRecords.value.sort((a, b) => b.timestamp - a.timestamp)
        saveHistoryRecordsToStorage()
      } catch (e) {
        console.error('保存历史记录失败:', e)
      }
    }

    // 保存历史记录到本地存储
    const saveHistoryRecordsToStorage = () => {
      try {
        localStorage.setItem('match_heatmap_history', JSON.stringify(historyRecords.value))
      } catch (e) {
        console.error('保存历史记录到本地存储失败:', e)
      }
    }

    // 加载历史记录
    const loadHistoryRecords = () => {
      try {
        const saved = localStorage.getItem('match_heatmap_history')
        if (saved) {
          const records = JSON.parse(saved)
          // 只加载当前比赛ID的记录
          historyRecords.value = records.filter(r => r.matchId === matchId.value)
          // 按时间戳倒序排列
          historyRecords.value.sort((a, b) => b.timestamp - a.timestamp)
        }
      } catch (e) {
        console.error('加载历史记录失败:', e)
      }
    }

    // 加载历史记录到当前画布
    const loadHistoryRecord = (index) => {
      if (index < 0 || index >= historyRecords.value.length) return
      
      const record = historyRecords.value[index]
      currentHistoryIndex.value = index
      
      // 恢复数据（只恢复对应队伍的数据，不影响另一个队伍）
      let pointsToLoad = JSON.parse(JSON.stringify(record.heatmapPoints))
      let iconsToLoad = JSON.parse(JSON.stringify(record.footballIcons))
      
      // 如果历史记录的时段与当前时段不同，需要反转坐标
      // 历史记录保存时的坐标是基于保存时的时段，需要转换到当前时段
      if (record.period !== selectedPeriod.value) {
        // 从半场切换到全场，或从全场切换到半场，需要反转坐标
        pointsToLoad = pointsToLoad.map(point => ({
          ...point,
          x: 100 - point.x
        }))
        iconsToLoad = iconsToLoad.map(icon => ({
          ...icon,
          x: 100 - icon.x
        }))
      }
      
      // 只恢复对应队伍的数据
      if (record.team === 1) {
        homeTeamPoints.value = pointsToLoad
        homeFootballIcons.value = iconsToLoad
      } else {
        awayTeamPoints.value = pointsToLoad
        awayFootballIcons.value = iconsToLoad
      }
      
      // 不改变 selectedPeriod，避免触发时段切换监听器影响另一个队伍
      
      // 更新热力图显示（只更新对应队伍）
      nextTick(() => {
        if (record.team === 1) {
          updateHomeHeatmap()
        } else {
          updateAwayHeatmap()
        }
      })
    }

    // 删除历史记录
    const deleteHistoryRecord = (index) => {
      if (index < 0 || index >= historyRecords.value.length) return
      
      if (confirm('确定要删除这条历史记录吗？')) {
        historyRecords.value.splice(index, 1)
        saveHistoryRecordsToStorage()
        
        // 如果删除的是当前选中的记录，清除选中状态
        if (currentHistoryIndex.value === index) {
          currentHistoryIndex.value = -1
        } else if (currentHistoryIndex.value > index) {
          // 如果删除的记录在当前选中记录之前，需要调整索引
          currentHistoryIndex.value--
        }
      }
    }

    // 格式化时间
    const formatTime = (timestamp) => {
      const date = new Date(timestamp)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${month}-${day} ${hours}:${minutes}`
    }

    // 自动保存数据变化
    watchEffect(() => {
      // 只在有数据时保存
      if (homeTeamPoints.value.length > 0 || awayTeamPoints.value.length > 0 || 
          homeFootballIcons.value.length > 0 || awayFootballIcons.value.length > 0) {
        saveData()
      }
    })

    // 监听笔刷大小变化，重新初始化热力图
    watch(brushSize, () => {
      if (homeHeatmapInstance && homeTeamPoints.value.length > 0) {
        nextTick(() => {
          initHomeHeatmap()
          updateHomeHeatmap()
        })
      }
      if (awayHeatmapInstance && awayTeamPoints.value.length > 0) {
        nextTick(() => {
          initAwayHeatmap()
          updateAwayHeatmap()
        })
      }
    })

    // 监听 matchId 变化，重新加载历史记录
    watch(matchId, () => {
      if (matchId.value) {
        loadHistoryRecords()
        currentHistoryIndex.value = -1
      }
    })

    // 监听时段选择变化，需要反转已有数据的坐标
    watch(selectedPeriod, (newPeriod, oldPeriod) => {
      // 如果从半场切换到全场，或从全场切换到半场，需要反转所有坐标
      if (oldPeriod !== undefined && newPeriod !== oldPeriod) {
        // 反转主队坐标
        if (homeTeamPoints.value.length > 0) {
          homeTeamPoints.value = homeTeamPoints.value.map(point => ({
            ...point,
            x: 100 - point.x
          }))
        }
        // 反转主队足球图标
        if (homeFootballIcons.value.length > 0) {
          homeFootballIcons.value = homeFootballIcons.value.map(icon => ({
            ...icon,
            x: 100 - icon.x
          }))
        }
        // 反转客队坐标
        if (awayTeamPoints.value.length > 0) {
          awayTeamPoints.value = awayTeamPoints.value.map(point => ({
            ...point,
            x: 100 - point.x
          }))
        }
        // 反转客队足球图标
        if (awayFootballIcons.value.length > 0) {
          awayFootballIcons.value = awayFootballIcons.value.map(icon => ({
            ...icon,
            x: 100 - icon.x
          }))
        }
        
        // 更新热力图显示
        nextTick(() => {
          updateHomeHeatmap()
          updateAwayHeatmap()
        })
      }
    })


    // 监听窗口大小变化
    const handleResize = () => {
      nextTick(() => {
        initHomeHeatmap()
        initAwayHeatmap()
        if (homeTeamPoints.value.length > 0) {
          updateHomeHeatmap()
        }
        if (awayTeamPoints.value.length > 0) {
          updateAwayHeatmap()
        }
      })
    }

    onMounted(() => {
      // 预加载足球图标图片
      preloadFootballIcon().catch(err => {
        console.warn('预加载足球图标失败，将在使用时重试:', err)
      })

      // 优先从路由参数获取 match_id
      const routeMatchId = route.query.match_id || route.params.match_id
      if (routeMatchId) {
        matchId.value = String(routeMatchId)
        hasRouteMatchId.value = true
        // 保存到本地存储
        saveMatchId()
      } else {
        // 如果没有路由参数，从本地存储加载
        const savedMatchId = localStorage.getItem('match_heatmap_match_id')
        if (savedMatchId) {
          matchId.value = savedMatchId
        }
      }

      // 加载历史记录
      loadHistoryRecords()

      // 加载保存的背景图片
      const savedBg = localStorage.getItem('match_heatmap_bg')
      if (savedBg) {
        backgroundImage.value = savedBg
      }

      // 加载换边状态
      const savedSwapped = localStorage.getItem('match_heatmap_swapped')
      if (savedSwapped === 'true') {
        isSwapped.value = true
      }

      // 初始化热力图
      nextTick(() => {
        initHomeHeatmap()
        initAwayHeatmap()
        
        // 加载保存的数据
        const saved = localStorage.getItem('match_heatmap')
        if (saved) {
          try {
            const data = JSON.parse(saved)
            homeTeamPoints.value = data.homePoints || []
            awayTeamPoints.value = data.awayPoints || []
            homeFootballIcons.value = data.homeFootballIcons || []
            awayFootballIcons.value = data.awayFootballIcons || []
            
            // 如果有数据，更新热力图
            if (homeTeamPoints.value.length > 0) {
              updateHomeHeatmap()
            }
            if (awayTeamPoints.value.length > 0) {
              updateAwayHeatmap()
            }
          } catch (e) {
            console.error('加载数据失败:', e)
          }
        }
      })

      // 监听窗口大小变化
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      homeFieldRef,
      awayFieldRef,
      homeHeatmapContainer,
      awayHeatmapContainer,
      homeAverageIntensity,
      awayAverageIntensity,
      isUploadingHome,
      isUploadingAway,
      backgroundImage,
      selectedPeriod,
      brushSize,
      heatIntensity,
      clickStyle,
      homeTeamPoints,
      awayTeamPoints,
      displayHomeFootballIcons,
      displayAwayFootballIcons,
      mousePosition,
      currentActiveTeam,
      isSwapped,
      qiuIcon,
      matchId,
      hasRouteMatchId,
      historyRecords,
      currentHistoryIndex,
      getHeatIntensityLabel,
      handleMouseDown,
      handleMouseMove,
      handleTouchStart,
      generateHomeHeatmap,
      generateAwayHeatmap,
      clearHeatmap,
      downloadHeatmap,
      undo,
      canUndoHome,
      canUndoAway,
      saveMatchId,
      loadHistoryRecord,
      deleteHistoryRecord,
      formatTime
    }
  }
}
</script>

<style scoped>
.heatmap-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #fda033 0%, #fda033 100%);
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.header {
  width: 100%;
  max-width: 1200px;
  margin-bottom: 20px;
  text-align: center;
}

.title {
  font-size: 28px;
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  margin: 0 0 10px 0;
}

.subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.match-id-input {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}

.match-id-label {
  font-size: 16px;
  color: white;
  font-weight: 500;
}

.match-id-field {
  padding: 8px 15px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 16px;
  min-width: 200px;
  outline: none;
  transition: all 0.3s ease;
}

.match-id-field::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.match-id-field:focus {
  border-color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.match-id-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}

.match-id-value {
  font-size: 16px;
  color: white;
  font-weight: 600;
  padding: 8px 15px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

/* iPad 和大屏设备适配 */
@media (min-width: 768px) {
  .title {
    font-size: 36px;
  }

  .subtitle {
    font-size: 20px;
  }

  .field-container {
    padding: 20px;
  }

  .control-panel {
    padding: 30px;
  }

  .stat-label {
    font-size: 18px;
  }

  .stat-value {
    font-size: 32px;
  }

  .btn {
    padding: 16px 32px;
    font-size: 18px;
  }

  .heatmap-preview h3 {
    font-size: 24px;
  }
}

/* iPad Pro 和大屏设备 */
@media (min-width: 1024px) {
  .title {
    font-size: 42px;
  }

  .subtitle {
    font-size: 22px;
  }

  .content {
    gap: 30px;
  }

  .field-container {
    padding: 25px;
  }

  .control-panel {
    padding: 35px;
  }

  .stat-label {
    font-size: 20px;
  }

  .stat-value {
    font-size: 36px;
  }

  .btn {
    padding: 18px 40px;
    font-size: 20px;
  }

  .heatmap-preview {
    padding: 30px;
  }

  .heatmap-preview h3 {
    font-size: 28px;
  }
}

.content {
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.fields-container {
  width: 100%;
  display: flex;
  gap: 180px;
  justify-content: center;
  flex-wrap: wrap;
}

.field-wrapper {
  flex: 1;
  min-width: 300px;
  max-width: 45%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.field-label {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.field-container-small {
  position: relative;
  width: 100%;
  background: white;
  border-radius: 15px;
  padding: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.field-container-small .team-badge {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px 16px;
  background: rgba(253, 160, 51, 0.9);
  color: white;
  font-size: 16px;
  font-weight: 600;
  border-radius: 20px;
  z-index: 10;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

.field-container-small .team-badge-left {
  left: -70px;
}

.field-container-small .team-badge-right {
  right: -70px;
}

.field-container {
  width: 100%;
  background: white;
  border-radius: 15px;
  padding: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

/* iPad 上增大球场容器 */
@media (min-width: 768px) {
  .field-container {
    padding: 25px;
    border-radius: 20px;
  }
}

.field {
  width: 100%;
  aspect-ratio: 622 / 341;
  background: #2d5016 center center / contain no-repeat;
  background-image: v-bind('`url(${backgroundImage})`');
  border: 3px solid white;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  cursor: crosshair;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.field-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(255, 255, 255, 0.8);
}

.center-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20%;
  height: 30%;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
}

.center-spot {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
}

.penalty-area {
  position: absolute;
  width: 15%;
  height: 40%;
  border: 2px solid rgba(255, 255, 255, 0.8);
  top: 50%;
  transform: translateY(-50%);
}

.penalty-area.left {
  left: 0;
  border-right: none;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.penalty-area.right {
  right: 0;
  border-left: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.goal-area {
  position: absolute;
  width: 8%;
  height: 20%;
  border: 2px solid rgba(255, 255, 255, 0.8);
  top: 50%;
  transform: translateY(-50%);
}

.goal-area.left {
  left: 0;
  border-right: none;
}

.goal-area.right {
  right: 0;
  border-left: none;
}

.penalty-spot {
  position: absolute;
  width: 3px;
  height: 3px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
}

.penalty-spot.left {
  left: 11%;
}

.penalty-spot.right {
  right: 11%;
}

.midline {
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  transform: translateX(-50%);
}

.heatmap-container-inner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .fields-container {
    gap: 120px;
  }
  
  .field-container-small .team-badge {
    padding: 6px 12px;
    font-size: 14px;
  }
  
  .field-container-small .team-badge-left {
    left: -50px;
  }
  
  .field-container-small .team-badge-right {
    right: -50px;
  }
}

.brush-cursor {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  pointer-events: none;
  z-index: 3;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
  transition: width 0.1s ease, height 0.1s ease, margin-left 0.1s ease, margin-top 0.1s ease;
}

.football-icons-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
}

.football-icon {
  position: absolute;
  cursor: pointer;
  pointer-events: auto;
  user-select: none;
  -webkit-user-select: none;
  transition: transform 0.2s ease;
}

.football-icon-img {
  width: 16px;
  height: 16px;
  display: block;
  border-radius: 50%;
  object-fit: cover;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.5));
}

.football-icon:hover {
  transform: translate(-50%, -50%) scale(1.2);
}

/* iPad 和大屏设备适配 */
@media (min-width: 768px) {
  .football-icon-img {
    width: 20px;
    height: 20px;
  }
}

.control-panel {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.stats {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
  gap: 20px;
  flex-wrap: wrap;
}

.stat-group {
  flex: 1;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: rgba(253, 160, 51, 0.05);
  border-radius: 10px;
}

.stat-group-title {
  font-size: 16px;
  font-weight: 600;
  color: #fda033;
  margin-bottom: 5px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.actions-left {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
  justify-content: flex-start;
}

.actions-center {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

.actions-right {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
  justify-content: flex-end;
}

/* 点击样式选择器（悬浮在左侧） */
.style-selector {
  position: fixed;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 1000;
  background: white;
  padding: 12px;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.style-btn {
  width: 48px;
  height: 48px;
  border: 2px solid #ddd;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  padding: 0;
  outline: none;
}

.style-btn:hover {
  border-color: #fda033;
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(253, 160, 51, 0.3);
}

.style-btn.active {
  border-color: #fda033;
  background: rgba(253, 160, 51, 0.1);
  box-shadow: 0 2px 8px rgba(253, 160, 51, 0.4);
}

.style-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .style-selector {
    left: 10px;
    padding: 8px;
  }
  
  .style-btn {
    width: 40px;
    height: 40px;
  }
  
  .style-icon {
    width: 24px;
    height: 24px;
  }
}

.btn {
  border: none;
  border-radius: 25px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #fda033 0%, #fda033 100%);
  color: white;
}

.btn-primary:active {
  transform: scale(0.95);
}

.btn-primary:hover {
  box-shadow: 0 4px 12px rgba(78, 205, 196, 0.4);
}

.btn-secondary {
  background: rgba(253, 160, 51,.2);
  color: #fda033;
  border: 2px solid #fda033;
}

.btn-secondary:active {
  transform: scale(0.95);
}

.btn-secondary:hover {
  background: rgba(102, 126, 234, 0.2);
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  width: 100%;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .actions {
    flex-direction: column;
    gap: 15px;
  }
  
  .actions-left,
  .actions-center,
  .actions-right {
    width: 100%;
    justify-content: center;
  }
}

/* 换边开关 */
.swap-toggle {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  background: rgba(253, 160, 51, 0.1);
  color: #fda033;
  border: 2px solid #fda033;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.swap-toggle:hover {
  background: rgba(253, 160, 51, 0.2);
  box-shadow: 0 2px 8px rgba(253, 160, 51, 0.3);
}

.swap-toggle input[type="checkbox"] {
  display: none;
}

.swap-toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.swap-toggle input[type="checkbox"]:checked ~ .swap-toggle-label {
  color: #fff;
}

.swap-toggle:has(input[type="checkbox"]:checked) {
  background: #fda033;
  color: white;
}

.swap-toggle:has(input[type="checkbox"]:checked) .swap-toggle-label {
  color: white;
}

.swap-toggle svg {
  width: 20px;
  height: 20px;
}

.team-select {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: center;
}

.team-select label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.team-select-dropdown {
  padding: 8px 12px;
  border: 2px solid #fda033;
  border-radius: 15px;
  background: white;
  color: #fda033;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  min-width: 100px;
}

.team-select-dropdown:hover {
  background: rgba(102, 126, 234, 0.05);
}

.team-select-dropdown:focus {
  border-color: #4ecdc4;
  box-shadow: 0 0 0 3px rgba(78, 205, 196, 0.1);
}

.heatmap-preview {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.heatmap-preview h3 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 20px;
  text-align: center;
}

.preview-field {
  width: 100%;
  aspect-ratio: 622 / 341;
  background: #2d5016 center center / contain no-repeat;
  background-image: v-bind('`url(${backgroundImage})`');
  border: 3px solid #ddd;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
}

/* 移动端适配 */
@media (max-width: 480px) {
  .heatmap-container {
    padding: 10px;
  }

  .title {
    font-size: 22px;
  }

  .subtitle {
    font-size: 14px;
  }

  .field-container {
    padding: 10px;
  }

  .control-panel {
    padding: 15px;
  }

  .stats {
    flex-direction: column;
    gap: 15px;
  }

  .stat-value {
    font-size: 20px;
  }

  .actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    padding: 14px 24px;
  }

}

@media (max-width: 360px) {
  .title {
    font-size: 20px;
  }
}

/* 历史记录样式（悬浮在右上方） */
.history-section {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 320px;
  max-width: calc(100vw - 40px);
  padding: 15px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

.history-title {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  flex-shrink: 0;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.history-list::-webkit-scrollbar {
  width: 6px;
}

.history-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb {
  background: rgba(253, 160, 51, 0.3);
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb:hover {
  background: rgba(253, 160, 51, 0.5);
}

.history-list::-webkit-scrollbar {
  width: 6px;
}

.history-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb {
  background: rgba(253, 160, 51, 0.3);
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb:hover {
  background: rgba(253, 160, 51, 0.5);
}

.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: rgba(253, 160, 51, 0.05);
  border: 2px solid rgba(253, 160, 51, 0.2);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.history-item:hover {
  background: rgba(253, 160, 51, 0.1);
  border-color: rgba(253, 160, 51, 0.4);
  transform: translateX(5px);
}

.history-item.active {
  background: rgba(253, 160, 51, 0.2);
  border-color: #fda033;
  box-shadow: 0 2px 8px rgba(253, 160, 51, 0.3);
}

.history-info {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
}

.history-team,
.history-period {
  padding: 4px 12px;
  background: #fda033;
  color: white;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
}

.history-time {
  color: #666;
  font-size: 14px;
  margin-left: auto;
}

.btn-delete {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 0, 0, 0.1);
  color: #ff4444;
  border-radius: 50%;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-delete:hover {
  background: rgba(255, 0, 0, 0.2);
  transform: scale(1.1);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .history-section {
    top: 10px;
    right: 10px;
    width: calc(100vw - 20px);
    max-width: 320px;
    padding: 12px;
    max-height: calc(100vh - 20px);
  }

  .history-title {
    font-size: 14px;
    margin-bottom: 10px;
  }

  .history-item {
    padding: 10px 12px;
  }

  .history-info {
    flex-wrap: wrap;
    gap: 8px;
  }

  .history-team,
  .history-period {
    font-size: 11px;
    padding: 3px 10px;
  }

  .history-time {
    font-size: 12px;
    width: 100%;
    margin-left: 0;
    margin-top: 5px;
  }
}
</style>

